import { InfoIcon } from "@vericus/cadmus-icons";
import { Button, Text, Tooltip } from "@vericus/cadmus-ui";

import { StudentSentimentChecksQuery } from "@/generated/graphql";
import { formatDateFull } from "@/utils/datetime/datetime";

import * as styles from "./sentiment-check-interval-card.css";

type Interval = StudentSentimentChecksQuery["studentSentimentChecks"][number];

interface Props {
  interval: Interval;
  onSubmit?: () => void;
}

export function SentimentCheckIntervalCard({ interval, onSubmit }: Props) {
  const { sentimentCheck } = interval;
  const checkNumber = interval.order;

  const isActive = new Date(interval.endTime) > new Date();
  const isSubmitted = !!sentimentCheck && !sentimentCheck.skipped;
  const isSkipped = !!sentimentCheck?.skipped;
  // Only the current (active) interval with no check can be submitted
  const canSubmit = isActive && (!sentimentCheck || isSkipped);

  const getBadgeStatus = (): keyof typeof styles.badge => {
    if (!isActive && isSkipped) return "SKIPPED";
    if (canSubmit) return "ACTIVE";
    if (isActive && isSubmitted) return "SUBMITTED";
    // Past interval (submitted or missed) → Closed
    return "CLOSED";
  };

  const badgeStatus = getBadgeStatus();

  const getButtonLabel = () => {
    if (isSubmitted) return "Submitted";
    return "Skipped";
  };

  const getDateText = () => {
    if (isSubmitted && sentimentCheck?.insertedAt) {
      return ` ${formatDateFull(new Date(sentimentCheck.insertedAt))}`;
    }
    return `${formatDateFull(new Date(interval.endTime))}`;
  };
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.surveyInfo}>
          <div className={styles.titleRow}>
            <Text kind="headingFive">Check {checkNumber}</Text>
          </div>
          <span className={styles.badge[badgeStatus]}>
            {badgeLabels[badgeStatus]}
          </span>
        </div>

        <div className={styles.buttonContainer}>
          {canSubmit ? (
            <>
              <Button kind="secondary" onClick={() => onSubmit?.()}>
                Complete Now
              </Button>
              <Tooltip
                content="You can only submit this once."
                width="auto"
                size="sm"
              >
                <InfoIcon label="info" size={20} />
              </Tooltip>
            </>
          ) : (
            <Button kind="outline" disabled className={styles.disabledButton}>
              {getButtonLabel()}
            </Button>
          )}
        </div>
      </div>

      <div className={styles.divider} />

      <Text kind="bodySm" className={styles.dateText}>
        <Text kind="bodySm" as="span" bold>
          {isSubmitted ? "Completed on:" : "Due:"}
        </Text>{" "}
        {getDateText()}
      </Text>
    </div>
  );
}

const badgeLabels: Record<string, string> = {
  ACTIVE: "Open Now",
  SUBMITTED: "Submitted",
  SKIPPED: "Skipped",
  CLOSED: "Closed",
};
